import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAppSelector } from '@/hooks'
import { selectTenant } from '@/features/auth/authSlice'
import { fetchIntegrationProviders } from '@/api/modules/integrations'
import { normalizeProvider } from './catalog'

/** React Query key namespace for the provider catalog; the tenant id is appended. */
export const PROVIDER_CATALOG_QUERY_KEY = ['integrations', 'providers']

const NO_PROVIDERS = Object.freeze([])

/**
 * The tenant's provider catalog (`GET /integrations/providers/`), fetched once
 * per tenant per session. Disabled or failing, it is an empty list, never a
 * throw: every reader falls back to the override table and the key.
 *
 * @returns {{providers: Object[], byKey: Record<string, Object>, isLoading: boolean, error: unknown}}
 */
export function useProviderCatalog() {
  const tenantId = useAppSelector(selectTenant)?.id
  const query = useQuery({
    queryKey: [...PROVIDER_CATALOG_QUERY_KEY, tenantId],
    queryFn: async () => {
      const envelope = await fetchIntegrationProviders()
      const resources = Array.isArray(envelope?.data) ? envelope.data : []
      return resources.map(normalizeProvider)
    },
    enabled: !!tenantId,
    staleTime: Infinity,
    retry: false,
  })
  const providers = query.data || NO_PROVIDERS
  const byKey = useMemo(() => Object.fromEntries(providers.map((provider) => [provider.id, provider])), [providers])
  return { providers, byKey, isLoading: query.isLoading, error: query.error ?? null }
}

export default useProviderCatalog
